import Formsy from 'lib/Formsy';
import * as React from 'react';

/**
 * ## [Formsy](https://github.com/christianalfoni/formsy-react) form wrapper.
 *
 *
 * ```js
 * <Form
 *   ref="form"
 *   onInvalid={() => this.setState({ disabled: true })}
 *   onValid={() => this.setState({ disabled: false })}
 *   onValidSubmit={() => console.log('onValidSubmit')}>
 *   <Button disabled={this.state.disabled}>
 *     Submit
 *   </Button>
 * </Form>
 * ```
 *
 *
 * # Implementation
 *
 * ## Sub-components
 *
 * Sub-components should primarily be Formsy-wrapped [Inputs](/internal/components/Input). In most
 * cases, it’s enough to pass in your own component into `FormInput`.
 *
 * ```js
 * <FormInput
 *   component={LocationInput}
 *   name="location"
 * />
 * ```
 *
 * But if necessary, roll your own. Look at the pre-existing examples:
 *
 * * FormCellInput (2 instances)
 * * FormCheckBox (2 instances)
 * * FormInput (43 instances)
 * * FormSelect (9 instances)
 *
 * ## `onValidSubmit`
 *
 * The `onValidSubmit` should deal with errors, generally by showing a
 * [Toast][/internal/components/Toast].
 *
 * ```js
 * onSubmit(value) {
 *   this.setState({ submitting: true });
 *
 *   const onDone = () => {
 *     this.setState({ submitting: false });
 *   }
 *
 *   return SomeActions.create(
 *      value.foo,
 *      value.bar
 *    ).then(() =>
 *      () => ToastActions.showSuccessToast('Success!'),
 *      () => ToastActions.showDefaultErrorToast())
 *    ).then(onDone);
 * }
 * ```
 *
 * ## Custom validations
 *
 * Modify [`Formsy.js`](https://github.com/remind101/r101-frontend/blob/master/src/lib/Formsy.js)…
 *
 * ```js
 * Formsy.customValidators.isRequired = function(values, value) {
 *   return value === null || value === undefined || value === '';
 * };
 * Formsy.addValidationRule('isRequired', Formsy.customValidators.isRequired);
 * ```
 *
 * …and use on Formsy-wrapped inputs:
 *
 * ```js
 * <FormInput
 *   validationErrors={{ isRequired: 'Input required' }}
 *   validations={{ isRequired: true }}
 * />
 * ```
 */

type FormsyFormProps = {
  className?: string;
  mapping?: (inputs: any) => { [key: string]: any };
  validationErrors?: { [key: string]: string };
  onSubmit?: (data: any, resetForm: () => void, invalidateForm: () => void) => void;
  onValid?: () => void;
  onInvalid?: () => void;
  onValidSubmit?: (
    model: any,
    resetForm: () => void,
    invalidateForm: (fields: { [key: string]: string }) => void
  ) => void;
  onChange?: (currentValues: { [key: string]: string }, isChanged: boolean) => void;
  children: React.ReactNode;
};

class Form extends React.Component<FormsyFormProps> {
  constructor(props: FormsyFormProps) {
    super(props);
    this.formsy = React.createRef();
  }

  formsy: React.RefObject<Formsy.Form>;

  // public
  getCurrentValues = () => {
    if (this.formsy.current) {
      return this.formsy.current.getCurrentValues();
    }
  };

  render() {
    const { children, ...props } = this.props;
    return (
      <Formsy.Form {...props} method="post" ref={this.formsy}>
        {children}
      </Formsy.Form>
    );
  }
}
export default Form;
